import React, { useState, useEffect } from 'react';
import { Leadership } from '../../types/Leadership';
import { LeadershipService } from '../../services/LeadershipService';
import { User, Quote } from 'lucide-react';

const LeadershipDisplay: React.FC = () => {
  const [leadership, setLeadership] = useState<Leadership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [filteredLeadership, setFilteredLeadership] = useState<Leadership[]>([]);

  // Use the exact same image URL logic as the working ChoirDisplay
  const getImageUrl = (image: any): string => {
    // Use S3 URL if available, otherwise fall back to base64 (for backward compatibility)
    if (image.s3Url) {
      return image.s3Url;
    }
    // Fallback for legacy base64 images (stored in s3Key field for backward compatibility)
    if (image.s3Key && image.s3Key.startsWith('data:')) {
      return image.s3Key;
    }
    // If no valid URL, return a placeholder or empty string
    return '';
  };

  useEffect(() => {
    fetchLeadership();
  }, []);

  const fetchLeadership = async () => {
    try {
      setLoading(true);
      const data = await LeadershipService.getAllActiveLeadership();
      setLeadership(data);
      setFilteredLeadership(data);
    } catch (error) {
      console.error('Error fetching leadership:', error);
      setError('Failed to load leadership information');
    } finally {
      setLoading(false);
    }
  };

  // Get filter tabs based on leadership titles/roles
  const getFilterTabs = (): string[] => {
    const titles = Array.from(new Set(leadership.map(leader => leader.title)));
    return ['all', ...titles];
  };

  // Filter leadership based on active filter
  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredLeadership(leadership);
    } else {
      setFilteredLeadership(leadership.filter(leader => leader.title === activeFilter));
    }
  }, [activeFilter, leadership]);

  const getFilterLabel = (filter: string): string => {
    if (filter === 'all') return 'All Leadership';
    return filter;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-lg">Loading leadership...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
        <div className="text-center text-white">
          <p className="text-lg">{error}</p>
        </div>
      </div>
    );
  }

  if (leadership.length === 0) {
    return (
      <div className="max-w-6xl mx-auto px-8 py-8">
        <div className="bg-white rounded-2xl p-8 shadow-lg">
          <div className="text-center">
            <User className="mx-auto h-16 w-16 text-gray-400 mb-6" />
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">No Leadership Profiles Available</h3>
            <p className="text-gray-600 text-lg">Check back later for leadership information.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-8 py-8">
      <div className="bg-white rounded-2xl p-8 shadow-lg">
        {/* Header */}
        <h2 className="text-3xl font-bold text-gray-800 mb-6 border-b-4 border-blue-500 pb-2">
          Our Leadership Team
        </h2>

        {/* Filter Tabs */}
        <div className="flex justify-center gap-4 mb-12 flex-wrap">
          {getFilterTabs().map((filter) => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                activeFilter === filter
                  ? 'bg-blue-500 text-white shadow-lg transform -translate-y-1'
                  : 'bg-blue-50 text-blue-600 border-2 border-blue-200 hover:bg-blue-500 hover:text-white hover:transform hover:-translate-y-1'
              }`}
            >
              {getFilterLabel(filter)}
            </button>
          ))}
        </div>

        {/* Leadership Grid */}
        <div className="space-y-8">
          {filteredLeadership.map((leader, index) => {
            const isEven = index % 2 === 1;

            return (
              <div
                key={leader.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className={`grid md:grid-cols-2 min-h-96 ${isEven ? 'md:grid-flow-dense' : ''}`}>
                  {/* Image Section */}
                  <div className={`relative overflow-hidden h-[400px] ${isEven ? 'md:col-start-2' : ''}`}>
                    {leader.image ? (
                      (() => {
                        const imageUrl = getImageUrl(leader.image);
                        if (!imageUrl) return null;

                        return (
                          <img
                            src={imageUrl}
                            alt={leader.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.log('Leadership image failed to load:', imageUrl);
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                            onLoad={() => {
                              console.log('Leadership image loaded successfully:', imageUrl);
                            }}
                          />
                        );
                      })()
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/80 to-blue-700/80"></div>
                        <User size={64} className="text-white relative z-10" />
                      </div>
                    )}
                  </div>

                  {/* Content Section */}
                  <div className={`p-8 flex flex-col justify-center ${isEven ? 'md:col-start-1' : ''}`}>
                    <div className="leadership-header mb-6">
                      <h3 className="text-3xl font-bold text-gray-800 mb-2">{leader.name}</h3>
                      <p className="text-xl text-blue-600 font-medium mb-4">{leader.title}</p>
                    </div>

                    {/* Quote */}
                    {leader.quote && (
                      <div className="mb-6 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                        <div className="flex items-start">
                          <Quote className="h-5 w-5 text-blue-500 mt-1 mr-3 flex-shrink-0" />
                          <p className="text-blue-800 italic leading-relaxed">
                            "{leader.quote}"
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Biography */}
                    <div className="prose prose-gray max-w-none">
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                        {leader.biography}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Call to Action - Contact Only */}
        <div className="mt-12 text-center">
          <div className="bg-blue-50 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Interested in Learning More?
            </h2>
            <p className="text-gray-600 mb-6">
              Our leadership team is passionate about music education and would love to hear from you.
            </p>
            <a
              href="#contact"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-300"
            >
              Contact Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadershipDisplay;
